import React, { useState, useEffect } from 'react';
import { useAuth, useLanguage } from '../context/AppContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { 
  MessageCircleQuestion, 
  Loader2, 
  CheckCircle, 
  Upload, 
  X,
  AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Catégories d'incidents
const CATEGORIES = [
  { value: 'paiement', label: 'Paiement', labelEn: 'Payment' },
  { value: 'billets', label: 'Billets', labelEn: 'Tickets' },
  { value: 'compte', label: 'Mon compte', labelEn: 'My Account' },
  { value: 'technique', label: 'Problème technique', labelEn: 'Technical Issue' },
  { value: 'autre', label: 'Autre', labelEn: 'Other' },
];

// Types d'utilisateurs
const USER_TYPES = [
  { value: 'fan', label: 'Fan / Acheteur', labelEn: 'Fan / Buyer' },
  { value: 'artiste', label: 'Artiste / Manager', labelEn: 'Artist / Manager' },
  { value: 'apporteur', label: 'Apporteur', labelEn: 'Business Provider' },
  { value: 'autre', label: 'Autre', labelEn: 'Other' },
];

export function SupportButton({ errorContext = null, variant = 'floating' }) {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [reference, setReference] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  
  const [formData, setFormData] = useState({
    userType: '',
    category: '',
    name: '',
    email: '',
    phone: '',
    subject: '',
    description: '',
  });

  // Pré-remplir si utilisateur connecté
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        email: user.email || '',
        phone: user.phone || '',
        userType: user.role === 'ARTISTE' || user.role === 'MANAGER' ? 'artiste' 
                : user.role === 'APPORTEUR' ? 'apporteur' 
                : user.role === 'FAN' ? 'fan' : '',
      }));
    }
  }, [user]);

  // Si errorContext est fourni, pré-remplir
  useEffect(() => {
    if (errorContext) {
      setFormData(prev => ({
        ...prev,
        category: 'technique',
        subject: errorContext.message ? `Erreur: ${errorContext.message.substring(0, 50)}` : 'Erreur technique',
        description: `Une erreur s'est produite:\n\nCode: ${errorContext.code || 'N/A'}\nMessage: ${errorContext.message || 'N/A'}\nEndpoint: ${errorContext.endpoint || 'N/A'}\n\nDétails supplémentaires:\n`,
      }));
      setIsOpen(true);
    }
  }, [errorContext]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.userType || !formData.category || !formData.name || !formData.email || !formData.subject || !formData.description) {
      toast.error(language === 'fr' ? 'Veuillez remplir tous les champs obligatoires' : 'Please fill all required fields');
      return;
    }

    if (formData.description.length < 20) {
      toast.error(language === 'fr' ? 'Veuillez décrire votre problème plus en détail (min 20 caractères)' : 'Please describe your issue in more detail (min 20 characters)');
      return;
    }

    setIsSubmitting(true);

    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/api/support/incidents`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...formData,
          pageUrl: window.location.href,
          userAgent: navigator.userAgent,
          errorContext: errorContext || null,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Si on a une pièce jointe, l'uploader
        if (attachment && data.incidentId) {
          setUploadingAttachment(true);
          const formDataFile = new FormData();
          formDataFile.append('file', attachment);
          
          try {
            await fetch(`${API_URL}/api/support/incidents/${data.incidentId}/attachment`, {
              method: 'POST',
              body: formDataFile,
            });
          } catch (err) {
            console.error('Failed to upload attachment:', err);
          }
          setUploadingAttachment(false);
        }

        setIsSuccess(true);
        setReference(data.reference);
        toast.success(language === 'fr' ? 'Demande envoyée avec succès' : 'Request sent successfully');
      } else {
        throw new Error(data.detail || 'Failed to submit');
      }
    } catch (error) {
      console.error('Support submission error:', error);
      toast.error(language === 'fr' ? 'Erreur lors de l\'envoi. Veuillez réessayer.' : 'Error sending request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsSuccess(false);
    setReference('');
    setAttachment(null);
    if (!user) {
      setFormData({
        userType: '',
        category: '',
        name: '',
        email: '',
        phone: '',
        subject: '',
        description: '',
      });
    } else {
      setFormData(prev => ({
        ...prev,
        subject: '',
        description: '',
        category: '',
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(language === 'fr' ? 'Fichier trop volumineux (max 5MB)' : 'File too large (max 5MB)');
        return;
      }
      setAttachment(file);
    }
  };

  // Bouton flottant ou inline selon le variant
  const triggerButton = variant === 'floating' ? (
    <button
      onClick={() => setIsOpen(true)}
      className="fixed bottom-6 right-6 z-50 bg-[#D4AF37] hover:bg-[#B8960C] text-black p-4 rounded-full shadow-lg transition-all hover:scale-105 group"
      title={language === 'fr' ? 'Contacter le support' : 'Contact Support'}
      data-testid="support-floating-btn"
    >
      <MessageCircleQuestion className="w-6 h-6" />
      <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-black text-white px-3 py-1 rounded text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
        {language === 'fr' ? 'Contacter le support' : 'Contact Support'}
      </span>
    </button>
  ) : (
    <Button
      onClick={() => setIsOpen(true)}
      variant={errorContext ? 'destructive' : 'outline'}
      size="sm"
      className="gap-2"
      data-testid="support-inline-btn"
    >
      {errorContext ? <AlertTriangle className="w-4 h-4" /> : <MessageCircleQuestion className="w-4 h-4" />}
      {errorContext 
        ? (language === 'fr' ? 'Reporter au support' : 'Report to Support')
        : (language === 'fr' ? 'Contacter le support' : 'Contact Support')
      }
    </Button>
  );

  return (
    <>
      {triggerButton}

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircleQuestion className="w-5 h-5 text-[#D4AF37]" />
              {language === 'fr' ? 'Contacter le support' : 'Contact Support'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? 'Décrivez votre problème et nous vous répondrons rapidement.'
                : 'Describe your issue and we\'ll get back to you soon.'}
            </DialogDescription>
          </DialogHeader>

          {isSuccess ? (
            <div className="py-8 text-center" data-testid="support-success">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">
                {language === 'fr' ? 'Demande envoyée !' : 'Request Sent!'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {language === 'fr' 
                  ? 'Votre référence : '
                  : 'Your reference: '}
                <span className="font-mono font-bold text-foreground">{reference}</span>
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                {language === 'fr'
                  ? 'Un email de confirmation vous a été envoyé. Notre équipe vous contactera sous 24-48h.'
                  : 'A confirmation email has been sent. Our team will contact you within 24-48h.'}
              </p>
              <Button onClick={handleClose} className="btn-primary">
                {language === 'fr' ? 'Fermer' : 'Close'}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="support-form">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Je suis *' : 'I am *'}</Label>
                  <Select
                    value={formData.userType}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, userType: value }))}
                  >
                    <SelectTrigger data-testid="support-user-type">
                      <SelectValue placeholder={language === 'fr' ? 'Choisir...' : 'Select...'} />
                    </SelectTrigger>
                    <SelectContent>
                      {USER_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {language === 'fr' ? type.label : type.labelEn}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Catégorie *' : 'Category *'}</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger data-testid="support-category">
                      <SelectValue placeholder={language === 'fr' ? 'Choisir...' : 'Select...'} />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {language === 'fr' ? cat.label : cat.labelEn}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Nom complet *' : 'Full Name *'}</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Jean Dupont"
                  data-testid="support-name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="email@example.com"
                    data-testid="support-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Téléphone' : 'Phone'}</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+221 77 xxx xx xx"
                    data-testid="support-phone"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Sujet *' : 'Subject *'}</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value.substring(0, 100) }))}
                  placeholder={language === 'fr' ? 'Résumez votre problème' : 'Summarize your issue'}
                  maxLength={100}
                  data-testid="support-subject"
                />
                <p className="text-xs text-muted-foreground text-right">{formData.subject.length}/100</p>
              </div>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Description *' : 'Description *'}</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder={language === 'fr' 
                    ? 'Décrivez votre problème en détail...'
                    : 'Describe your issue in detail...'}
                  rows={4}
                  data-testid="support-description"
                />
                <p className="text-xs text-muted-foreground">
                  {language === 'fr' ? 'Minimum 20 caractères' : 'Minimum 20 characters'} ({formData.description.length})
                </p>
              </div>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Pièce jointe (optionnel)' : 'Attachment (optional)'}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,.gif"
                    onChange={handleFileChange}
                    className="hidden"
                    id="support-attachment"
                  />
                  <label
                    htmlFor="support-attachment"
                    className="flex items-center gap-2 px-4 py-2 border rounded-md cursor-pointer hover:bg-accent transition-colors"
                  >
                    <Upload className="w-4 h-4" />
                    {language === 'fr' ? 'Choisir un fichier' : 'Choose file'}
                  </label>
                  {attachment && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="truncate max-w-[150px]">{attachment.name}</span>
                      <button
                        type="button"
                        onClick={() => setAttachment(null)}
                        className="text-destructive hover:text-destructive/80"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  JPG, PNG, PDF, GIF - Max 5MB
                </p>
              </div>

              {errorContext && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                  <p className="text-xs text-destructive font-medium mb-1">
                    {language === 'fr' ? 'Contexte de l\'erreur (automatique)' : 'Error Context (automatic)'}
                  </p>
                  <p className="text-xs text-muted-foreground font-mono">
                    Code: {errorContext.code || 'N/A'} | {errorContext.endpoint || window.location.pathname}
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1"
                >
                  {language === 'fr' ? 'Annuler' : 'Cancel'}
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || uploadingAttachment}
                  className="flex-1 btn-primary"
                  data-testid="support-submit"
                >
                  {isSubmitting || uploadingAttachment ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {language === 'fr' ? 'Envoi...' : 'Sending...'}
                    </>
                  ) : (
                    language === 'fr' ? 'Envoyer' : 'Send'
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// Hook pour reporter une erreur depuis un toast
export function useReportError() {
  const [errorToReport, setErrorToReport] = useState(null);

  const reportError = (error) => {
    setErrorToReport({
      code: error?.status || error?.code || 500,
      message: error?.message || error?.detail || 'Unknown error',
      endpoint: error?.endpoint || window.location.pathname,
    });
  };

  const clearError = () => setErrorToReport(null);

  return { errorToReport, reportError, clearError };
}

export default SupportButton;
